import { useEffect, useState } from 'react'
import type { EventBus } from '../core/EventBus'

/**
 * Minimal surface area of `DataLayer` that this indicator depends on. Keeping
 * it structural avoids importing the concrete `DataLayer` class so the
 * component is trivial to test with a fake.
 */
interface LoadingOlderSource {
  isLoadingOlder(): boolean
}

export interface LoadingOlderIndicatorProps {
  dataLayer: LoadingOlderSource
  eventBus: EventBus
  /** Vertical offset from the chart canvas top edge. EdgeChart passes the
   * `IndicatorLegend`'s bottom edge so the indicator never overlaps active
   * indicator rows. Defaults to 8 for callers without indicator-aware layout. */
  top?: number
  /** Horizontal offset (px, default 8) — used to clear the floating drawing bar. */
  left?: number
}

/**
 * Small "Loading older bars…" pill pinned to the top-left of the chart canvas
 * while a scroll-back fetch is in flight. Hides when no fetch is active and
 * when the floor has been reached (the latter is communicated via `loading=false`
 * on `data:loadingOlderChanged`).
 *
 * Spec: docs/superpowers/specs/2026-05-04-chart-lookback-and-routing-design.md
 */
export function LoadingOlderIndicator({
  dataLayer,
  eventBus,
  top = 8,
  left = 8,
}: LoadingOlderIndicatorProps) {
  const [loading, setLoading] = useState<boolean>(() => dataLayer.isLoadingOlder())

  useEffect(() => {
    const handler = (payload: { loading: boolean }) => {
      setLoading(payload.loading)
    }
    eventBus.on('data:loadingOlderChanged', handler)
    return () => {
      eventBus.off('data:loadingOlderChanged', handler)
    }
  }, [eventBus])

  if (!loading) return null

  return (
    <div
      style={{
        position: 'absolute',
        top,
        left,
        zIndex: 10,
        padding: '3px 8px',
        borderRadius: 10,
        background: 'rgba(19,23,34,0.85)',
        border: '1px solid #2a2e39',
        color: '#d1d4dc',
        fontSize: 11,
        lineHeight: 1.2,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}
      role="status"
      aria-live="polite"
    >
      Loading older bars…
    </div>
  )
}
